/**
 * CWT / COSE_Sign1 / base45 — compact signed ticket token for QR codes.
 *
 * This is the ONLY signing and verification primitive for ticket tokens.
 * Never import WebCrypto's `subtle.sign`/`subtle.verify` directly for
 * ticket verification — always go through the functions in this module.
 *
 * Wire format: "EKTK1" + base45( COSE_Sign1_tagged )
 *
 * References:
 *   RFC 8392 — CBOR Web Token (CWT)
 *   RFC 9052 — COSE Structures (COSE_Sign1 = tag 18)
 *   RFC 9285 — Base45 encoding
 */
import { Encoder, Tag, Decoder } from "cbor-x";
import * as cborX from "cbor-x";
import { base45Encode, base45Decode } from "./base45.js";
import { CWT_PREFIX, CWT_ISSUER, CWT_AUDIENCE, CWT_ALG_ES256, COSE_SIGN1_TAG, CWT_CLAIM, CWT_PRIVATE_CLAIM, COSE_HEADER, } from "./constants.js";
const cborModule = cborX;
const setCborSizeLimits = cborModule.setSizeLimits ?? cborModule.setMaxLimits;
if (setCborSizeLimits === undefined) {
    throw new Error("cbor-x size-limit API not found — check cbor-x version");
}
setCborSizeLimits({ maxArraySize: 16, maxMapSize: 32, maxObjectSize: 32 });
const cborDecoder = new Decoder({ mapsAsObjects: false });
/**
 * `tagUint8Array: false` is load-bearing, for two reasons.
 *
 * 1. **Correctness (RFC 9052).** COSE requires the protected header, payload,
 *    external_aad and signature to be plain byte strings (major type 2). cbor-x's
 *    default wraps a `Uint8Array` in tag 64 ("uint8 typed array"), which is not
 *    valid COSE and would not interoperate with any other CWT implementation.
 *
 * 2. **Determinism across runtimes.** That tagging is `Buffer`-dependent: under
 *    Node cbor-x emits the tag, in a browser (no `Buffer`) it emits a bare bstr.
 *    The backend signs under Node and the staff PWA rebuilds the Sig_structure in
 *    a browser, so the two computed *different bytes over the same inputs* and
 *    every ticket failed to verify on a real phone — while every Node-side test
 *    passed, because vitest also runs under Node.
 *
 * Encode ticket tokens through this encoder only; never the bare `encode()`.
 */
const cborEncoder = new Encoder({ mapsAsObjects: false, tagUint8Array: false });
const encode = (value) => cborEncoder.encode(value);
/** QR alphanumeric capacity tops out at ~4300 chars; a real ticket token is
 *  ~250. Reject anything wildly larger before decoding. */
const MAX_TOKEN_LENGTH = 1024;
// ── External AAD ────────────────────────────────────────────────────
function buildExternalAad(issuer, audience) {
    return new Uint8Array(encode([issuer, audience]));
}
// ── COSE protected header ───────────────────────────────────────────
function encodeProtectedHeader(kid) {
    const map = new Map();
    map.set(COSE_HEADER.ALG, CWT_ALG_ES256);
    map.set(COSE_HEADER.KID, new TextEncoder().encode(kid));
    return new Uint8Array(encode(map));
}
// ── Claims ↔ CBOR ──────────────────────────────────────────────────
function encodeClaims(claims) {
    const map = new Map();
    map.set(CWT_CLAIM.IAT, claims.iat);
    map.set(CWT_CLAIM.CTI, claims.cti);
    map.set(CWT_PRIVATE_CLAIM.EVENT_ID, claims.event_id);
    map.set(CWT_PRIVATE_CLAIM.EVENT_DATE_DAYS, claims.event_date_days);
    map.set(CWT_PRIVATE_CLAIM.CATEGORY, claims.category);
    map.set(CWT_PRIVATE_CLAIM.PRICE_WORTH, claims.price_worth);
    map.set(CWT_PRIVATE_CLAIM.CURRENCY, claims.currency);
    map.set(CWT_PRIVATE_CLAIM.ISSUED_BY, claims.issued_by);
    map.set(CWT_PRIVATE_CLAIM.IS_TEST, claims.is_test);
    return new Uint8Array(encode(map));
}
function decodeClaims(payload) {
    const map = cborDecoder.decode(payload);
    return {
        iat: map.get(CWT_CLAIM.IAT),
        cti: map.get(CWT_CLAIM.CTI),
        event_id: map.get(CWT_PRIVATE_CLAIM.EVENT_ID),
        event_date_days: map.get(CWT_PRIVATE_CLAIM.EVENT_DATE_DAYS),
        category: map.get(CWT_PRIVATE_CLAIM.CATEGORY),
        price_worth: map.get(CWT_PRIVATE_CLAIM.PRICE_WORTH),
        currency: map.get(CWT_PRIVATE_CLAIM.CURRENCY),
        issued_by: map.get(CWT_PRIVATE_CLAIM.ISSUED_BY),
        is_test: map.get(CWT_PRIVATE_CLAIM.IS_TEST),
    };
}
// ── Sig_structure1 (RFC 9052 §4.4) ─────────────────────────────────
function buildSigStructure(protectedBytes, externalAad, payloadBytes) {
    return new Uint8Array(encode(["Signature1", protectedBytes, externalAad, payloadBytes]));
}
// ── ECDSA helpers ───────────────────────────────────────────────────
const ES256_PARAMS = { name: "ECDSA", hash: "SHA-256" };
const EXPECTED_SIG_LENGTH = 64; // P-256 r||s in IEEE P1363
/**
 * Build and sign a COSE_Sign1 ticket token, base45-encode it, and
 * prepend the `EKTK1` prefix. Returns the complete QR payload string.
 */
export async function signTicketCwt(claims, opts) {
    const issuer = opts.issuer ?? CWT_ISSUER;
    const audience = opts.audience ?? CWT_AUDIENCE;
    const protectedBytes = encodeProtectedHeader(opts.kid);
    const payloadBytes = encodeClaims(claims);
    const externalAad = buildExternalAad(issuer, audience);
    const toBeSigned = buildSigStructure(protectedBytes, externalAad, payloadBytes);
    // Uint8Array→BufferSource variance: TS DOM lib is overly strict about
    // ArrayBufferLike vs ArrayBuffer; both Node and browser accept Uint8Array.
    const sigBuf = await crypto.subtle.sign(ES256_PARAMS, opts.privateKey, toBeSigned);
    const signature = new Uint8Array(sigBuf);
    const coseSign1 = new Tag([protectedBytes, new Map(), payloadBytes, signature], COSE_SIGN1_TAG);
    const coseBytes = new Uint8Array(encode(coseSign1));
    return CWT_PREFIX + base45Encode(coseBytes);
}
// ── Verify ──────────────────────────────────────────────────────────
/**
 * Verify a compact ticket token: strip prefix → base45 decode →
 * parse COSE_Sign1 → hard-pin algorithm → verify ES256 signature →
 * decode and return claims.
 *
 * Rejects: wrong/missing algorithm, unknown kid, bad signature,
 * iss/aud mismatch (via external_aad), malformed structure.
 */
export async function verifyTicketCwt(token, opts) {
    // ── 1. length guard + base45 decode ──
    if (token.length > MAX_TOKEN_LENGTH) {
        throw new CwtVerifyError("Token exceeds maximum length");
    }
    const coseBytes = base45Decode(token);
    // ── 2. parse COSE_Sign1 (tag 18 wrapping a 4-element array) ──
    const decoded = cborDecoder.decode(coseBytes);
    if (!(decoded instanceof Tag) || decoded.tag !== COSE_SIGN1_TAG) {
        throw new CwtVerifyError("Not a COSE_Sign1 structure");
    }
    const arr = decoded.value;
    if (!Array.isArray(arr) || arr.length !== 4) {
        throw new CwtVerifyError("Malformed COSE_Sign1 array");
    }
    const [protectedBytes, unprotectedMap, payloadBytes, signature] = arr;
    if (!(protectedBytes instanceof Uint8Array)) {
        throw new CwtVerifyError("Protected header must be a byte string");
    }
    if (!(payloadBytes instanceof Uint8Array)) {
        throw new CwtVerifyError("Payload must be a byte string");
    }
    if (!(signature instanceof Uint8Array) || signature.length !== EXPECTED_SIG_LENGTH) {
        throw new CwtVerifyError("Signature must be 64 bytes");
    }
    // ── 3. decode + hard-pin protected header ──
    const protectedHeader = cborDecoder.decode(protectedBytes);
    const alg = protectedHeader.get(COSE_HEADER.ALG);
    if (alg !== CWT_ALG_ES256) {
        throw new CwtVerifyError(`Algorithm ${String(alg)} not allowed; only ES256 (-7) accepted`);
    }
    const kidRaw = protectedHeader.get(COSE_HEADER.KID);
    if (!(kidRaw instanceof Uint8Array)) {
        throw new CwtVerifyError("kid must be in the protected header as bstr");
    }
    const kid = new TextDecoder().decode(kidRaw);
    // ── 4. reject alg/kid in unprotected header (unauthenticated!) ──
    if (unprotectedMap instanceof Map) {
        if (unprotectedMap.has(COSE_HEADER.ALG)) {
            throw new CwtVerifyError("alg in unprotected header is forbidden");
        }
        if (unprotectedMap.has(COSE_HEADER.KID)) {
            throw new CwtVerifyError("kid in unprotected header is forbidden");
        }
    }
    // ── 5. resolve public key ──
    const publicKey = await opts.resolveKey({ alg, kid });
    // ── 6. build Sig_structure1 and verify ──
    const issuer = opts.issuer ?? CWT_ISSUER;
    const audience = opts.audience ?? CWT_AUDIENCE;
    const externalAad = buildExternalAad(issuer, audience);
    const toBeVerified = buildSigStructure(protectedBytes, externalAad, payloadBytes);
    const valid = await crypto.subtle.verify(ES256_PARAMS, publicKey, signature, toBeVerified);
    if (!valid) {
        throw new CwtVerifyError("Signature verification failed");
    }
    // ── 7. decode claims ──
    const claims = decodeClaims(payloadBytes);
    return {
        claims,
        protectedHeader: { alg, kid },
    };
}
// ── QR prefix helpers ───────────────────────────────────────────────
/**
 * Strip the `EKTK1` prefix from a QR payload string. Returns the base45
 * body, or null if the prefix is missing / payload is empty.
 */
export function extractCwtFromQr(qrPayload) {
    if (!qrPayload.startsWith(CWT_PREFIX))
        return null;
    const body = qrPayload.slice(CWT_PREFIX.length).trim();
    return body.length > 0 ? body : null;
}
// ── Diagnostics ─────────────────────────────────────────────────────
async function sha4(bytes) {
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
        .slice(0, 4)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}
/**
 * Short digests of every input that the ticket signature covers. Hashes only —
 * no key material, no forgeable data. The signer (`seed-ticket`) and the verifier
 * (staff app scan Details) both print this, so when a token fails to verify you
 * can tell at a glance *which* input diverged — the payload, the protected header,
 * the external AAD (iss/aud), the assembled Sig_structure, or the signature — and
 * stop guessing.
 *
 * Takes the base45 body (post-`extractCwtFromQr`), same as `verifyTicketCwt`.
 */
export async function cwtDiagnostics(token, opts = {}) {
    try {
        const coseBytes = base45Decode(token);
        const decoded = cborDecoder.decode(coseBytes);
        if (!(decoded instanceof Tag))
            return "diag: not a COSE_Sign1 tag";
        const arr = decoded.value;
        const [protectedBytes, , payloadBytes, signature] = arr;
        const externalAad = buildExternalAad(opts.issuer ?? CWT_ISSUER, opts.audience ?? CWT_AUDIENCE);
        const sigStructure = buildSigStructure(protectedBytes, externalAad, payloadBytes);
        return (`cose=${coseBytes.byteLength}B prot=${await sha4(protectedBytes)} ` +
            `payload=${await sha4(payloadBytes)} aad=${await sha4(externalAad)} ` +
            `sigstruct=${await sha4(sigStructure)} sig=${await sha4(signature)}`);
    }
    catch (err) {
        return `diag failed: ${err.message}`;
    }
}
// ── Error class ─────────────────────────────────────────────────────
export class CwtVerifyError extends Error {
    constructor(message) {
        super(message);
        this.name = "CwtVerifyError";
    }
}
// ── Re-export constants for verifier call sites ─────────────────────
export const TicketCwtConstants = {
    ISSUER: CWT_ISSUER,
    AUDIENCE: CWT_AUDIENCE,
    ALG: CWT_ALG_ES256,
    PREFIX: CWT_PREFIX,
};
//# sourceMappingURL=cwt.js.map